import React, { ReactNode, useEffect, useRef } from 'react';
import BottomActionBar from '../../../../components/BottomActionBar';
import { formatCurrency } from '../../../../utils/formatNumbers';
import {
  BottomChangeTextWrapper,
  BottomItemsWrapper,
  ChangeText,
  ErrorText,
  InformationPanelWrapper,
  SummaryItemsWrapper,
  Text,
  TotalPriceText,
  TotalPriceTitle,
  WrapperText
} from './style';
import { OptionsProps } from './CartProductTable';
import { addToast } from '../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../store/ducks/toast/types';
import { useDispatch } from 'react-redux';
import TextTooltipInformation from '../CheckoutMVP/TextTooltipInformation';
import { InformationPanel, Tag } from '@gerdau/hefesto-mais-web';
import { GetFlag } from '../../../../utils/featureFlag';
import { NewCartItem } from '../../../../services/MVP/models';
import { paymentAnticipated } from '../../../../services/MVP/utils';

const INFORMATION_PANEL_KEY = '@egerdau-web/bottom-information-panel';

interface SummaryBottomProps {
  totalPrice: string;
  cartItems?: NewCartItem[];
  loading: boolean;
  syncLoading?: boolean;
  hasProducts: boolean;
  showLoadNewPriceButton?: boolean;
  isBlockedCustomer: boolean;
  goToCheckout?: boolean;
  allItemsFailed?: boolean;
  onLoadNewPrice: (props: OptionsProps) => void;
  handlePrevious: () => void;
  handleNext: () => void;
  handleChangePaymentMethod: () => void;
}

export function InformationPanelContainer({
  refBtn,
  children,
  width,
  title,
  text
}: {
  refBtn: React.Ref<HTMLButtonElement>;
  children: ReactNode;
  width: string;
  title: string;
  text: string;
}) {
  return (
    <InformationPanelWrapper>
      <InformationPanel
        classNamePopup="informationPanelBottom"
        position="top center"
        panelWidth={width}
        title={title}
        text={text}
        trigger={
          <div>
            <button ref={refBtn}></button>
          </div>
        }
        type="text"
      />
      {children}
    </InformationPanelWrapper>
  );
}

const SummaryText = ({
  totalPrice,
  currentPaymentTermsDescription,
  showLoadNewPriceButton,
  handleChangePaymentMethod,
  syncLoading
}: {
  totalPrice: string;
  currentPaymentTermsDescription?: string;
  showLoadNewPriceButton?: boolean;
  handleChangePaymentMethod: () => void;
  syncLoading?: boolean;
}) => {
  const informationPanelButtonRef = useRef<HTMLButtonElement>(null);
  const newCartEndpoint = GetFlag('new_cart_endpoints');

  useEffect(() => {
    if (
      informationPanelButtonRef.current &&
      sessionStorage.getItem(INFORMATION_PANEL_KEY) === null
    ) {
      informationPanelButtonRef.current.click();
      sessionStorage.setItem(INFORMATION_PANEL_KEY, 'true');
    }
  }, [informationPanelButtonRef]);

  if (showLoadNewPriceButton) {
    return (
      <WrapperText>
        <span style={{ color: '#FF7703', fontSize: '14px' }}>
          Configurações alteradas. Atualize o preço do seu pedido.
        </span>
      </WrapperText>
    );
  }
  if (newCartEndpoint) {
    return (
      <WrapperText direction="row">
        <SummaryItemsWrapper>
          <TextTooltipInformation
            title="O preço pode sofrer alterações. 
Fale com o seu vendedor."
            placement="topLeft"
          >
            <TotalPriceTitle>Preço total</TotalPriceTitle>
          </TextTooltipInformation>
          <TotalPriceText>R${totalPrice}</TotalPriceText>
        </SummaryItemsWrapper>

        <BottomItemsWrapper>
          <SummaryItemsWrapper>
            <InformationPanelContainer
              title="Condição de pagamento para todos
os itens do carrinho"
              text="Agora todos os itens do seu carrinho assumem a mesma condição de pagamento."
              width="368px"
              refBtn={informationPanelButtonRef}
            >
              <TextTooltipInformation
                title="Forma de pagamento selecionada
              para todos os produtos deste carrinho."
                placement="topLeft"
              >
                <TotalPriceTitle>Condição de pagamento </TotalPriceTitle>
              </TextTooltipInformation>
              <Tag variant="light">{currentPaymentTermsDescription}</Tag>
            </InformationPanelContainer>
          </SummaryItemsWrapper>

          <BottomChangeTextWrapper>
            <ChangeText
              onClick={!syncLoading ? handleChangePaymentMethod : undefined}
              style={
                syncLoading
                  ? {
                      opacity: 0.5,
                      cursor: 'not-allowed',
                      pointerEvents: 'none'
                    }
                  : undefined
              }
            >
              Alterar
            </ChangeText>
          </BottomChangeTextWrapper>
        </BottomItemsWrapper>
      </WrapperText>
    );
  }

  return (
    <WrapperText>
      <b>Preço total: R${totalPrice}</b>
      <Text style={{ margin: 0 }}>
        O preço pode sofrer alterações. Fale com o seu vendedor.
      </Text>
    </WrapperText>
  );
};

export const SummaryBottom = ({
  totalPrice,
  loading,
  syncLoading,
  hasProducts,
  handlePrevious,
  handleNext,
  onLoadNewPrice,
  showLoadNewPriceButton,
  goToCheckout,
  cartItems,
  handleChangePaymentMethod,
  isBlockedCustomer,
  allItemsFailed
}: SummaryBottomProps) => {
  if (loading || !hasProducts || !cartItems) {
    return null;
  }

  const referenceValue = cartItems[0]?.price.paymentTermsDescription;

  const allEqual = cartItems.every(
    (item) => item.price.paymentTermsDescription === referenceValue
  );

  const commonDescription = allEqual
    ? referenceValue
    : cartItems[0].price.paymentTermsDescription || '';

  const currentPaymentTermsDescription = commonDescription;

  const blockedUserMessage =
    isBlockedCustomer &&
    !paymentAnticipated.includes(cartItems[0].price.paymentTerms);

  const [showToast, setShowToast] = React.useState(false);

  React.useEffect(() => {
    if (!showLoadNewPriceButton) {
      setShowToast(false);
    }
  }, [showLoadNewPriceButton]);

  const dispatch = useDispatch();

  if (!showToast && showLoadNewPriceButton && !goToCheckout) {
    setShowToast(true);
    dispatch(
      addToast({
        type: EToastTypes.WARNING,
        message: 'Configurações de produto alteradas.',
        durationInMS: 5000,
        labelActionOne: 'Atualizar preços',
        handleClickActionOne: () => {
          setShowToast(false);
          onLoadNewPrice({ updatePrice: true });
        }
      })
    );
  }

  if (allItemsFailed) {
    return (
      <BottomActionBar
        previousBtn={{
          fn: handlePrevious,
          name: 'previousBtn',
          text: 'Adicionar produtos',
          disabled: loading || syncLoading
        }}
        nextBtn={{
          fn: handleNext,
          name: 'nextBtn',
          text: 'Avançar para revisão',
          loading: syncLoading,
          disabled: syncLoading
        }}
      >
        <WrapperText>
          <ErrorText>Não foi possível processar os itens do pedido.</ErrorText>
        </WrapperText>
      </BottomActionBar>
    );
  }

  return (
    <BottomActionBar
      {...(showLoadNewPriceButton &&
        onLoadNewPrice && {
          thirdBtn: {
            fn: () => {
              setShowToast(false);
              onLoadNewPrice({ updatePrice: true });
            },
            name: 'thirdBtn',
            text: 'Atualizar preços',
            disabled: loading || syncLoading,
            loading: syncLoading,
            fullWidth: true
          }
        })}
      previousBtn={{
        fn: handlePrevious,
        name: 'previousBtn',
        text: 'Adicionar produtos',
        disabled: loading || syncLoading
      }}
      nextBtn={{
        fn: handleNext,
        name: 'nextBtn',
        text: 'Avançar para revisão',
        disabled: loading || syncLoading,
        loading: syncLoading
      }}
    >
      <SummaryText
        totalPrice={formatCurrency(totalPrice)}
        showLoadNewPriceButton={showLoadNewPriceButton}
        handleChangePaymentMethod={handleChangePaymentMethod}
        syncLoading={syncLoading}
        currentPaymentTermsDescription={
          blockedUserMessage ? '' : currentPaymentTermsDescription
        }
      />
    </BottomActionBar>
  );
};
